import styled from '@emotion/styled';

import { overflowHyphens, resetListStyles } from '@hrworks/design-system/utilities';
import { Card as _Card } from '@hrworks/sui-core/Card';

import { mq } from '../../util/mediaQueries';

const Card = styled(_Card)(({ theme }) => [
  overflowHyphens,
  {
    textDecoration: 'none',
    color: 'inherit',
    backgroundColor: theme.stellenportal?.cardStyle?.color,
    padding: `${theme.eclipseTier1.spacing['16px']} ${theme.eclipseTier1.spacing['20px']}`,
    '> :not(:last-child)': {
      borderBottom: 'none',
    },
    // Kein will-change: transform, sonst wird der Kartentext auf skalierten Displays unscharf.
    [mq.conditionalTransition]: {
      transition: `transform ${theme.variables.animationDuration.long}`,
    },
    [mq.supportsHover]: {
      ':hover': {
        transform: 'scale(1.01)',
        cursor: 'pointer',
      },
    },
  },
]);

const Content = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 160px',
  gap: theme.eclipseTier1.spacing['32px'],
  [mq.isSmallDevice]: {
    gridTemplateColumns: '1fr',
    gap: theme.eclipseTier1.spacing['8px'],
  },
}));

const Main = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: theme.eclipseTier1.spacing['8px'],
}));

const DESCRIPTION_LINES = 2;
const DESCRIPTION_LINE_HEIGHT = 1.5;

const Description = styled.div({
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: DESCRIPTION_LINES,
  WebkitBoxOrient: 'vertical',
  // Die Höhe muss zur tatsächlichen Zeilenhöhe passen, sonst legt WebKit (iOS) die Zeilen
  // übereinander. Das Portal erlaubt kundenspezifische Schriftart und -größe
  // (siehe RootLayout), daher wird lineHeight hier fixiert statt geerbt und die Höhe
  // daraus berechnet – die vorherige feste Angabe von 3em galt nur für lineHeight 1.5.
  lineHeight: DESCRIPTION_LINE_HEIGHT,
  height: `${DESCRIPTION_LINES * DESCRIPTION_LINE_HEIGHT}em`,
  // Der eingebettete HTML-Inhalt ist beliebiges Markup. Ohne Reset bringen p/ul die
  // UA-Margins (1em oben/unten) mit, die im -webkit-box nicht kollabieren und den
  // Zeilenfluss innerhalb der geklammerten Höhe verschieben.
  '> *': {
    margin: 0,
  },
  'p, ul, ol, h1, h2, h3, h4, h5, h6': {
    margin: 0,
    lineHeight: 'inherit',
  },
  ul: [resetListStyles],
});

const Sidebar = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.eclipseTier1.spacing['4px'],
  minWidth: 0,
  [mq.isSmallDevice]: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: theme.eclipseTier1.spacing['16px'],
  },
}));

const SidebarElement = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.eclipseTier1.spacing['4px'],
  minWidth: 0,
}));

const SidebarElementText = styled.span({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  minWidth: 0,
});

const MoreBadge = styled.span(({ theme }) => ({
  fontSize: '0.75rem',
  backgroundColor: theme.stellenportal?.fontStyle?.color || theme.eclipseTier2Color.text.default,
  color: theme.stellenportal?.cardStyle?.color || theme.eclipseTier2Color.surface.structure,
  padding: `${theme.eclipseTier1.spacing['2px']} ${theme.eclipseTier1.spacing['4px']}`,
  borderRadius: theme.eclipseTier1.radius['4px'],
  fontWeight: 500,
  marginLeft: theme.eclipseTier1.spacing['4px'],
  lineHeight: 1,
  flexShrink: 0,
}));

export const S = {
  Card,
  Content,
  Main,
  Description,
  Sidebar,
  SidebarElement,
  SidebarElementText,
  MoreBadge,
} as const;
